// src/components/KakaoMap.jsx
// ─────────────────────────────────────────────────────────────
//  카카오 지도 컴포넌트
//    - 공고 커스텀 마커
//    - 현재 위치 표시 + 이동
//    - 지도 영역 내 공고 필터 (이 지역 재검색)
//    - 마커 클릭 → 공고 상세 콜백
// ─────────────────────────────────────────────────────────────

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  waitForKakao, createMap, renderJobMarkers, clearMarkers,
  getCurrentPosition, createCurrentLocationMarker,
  panTo, getJobsInBounds, fitBoundsToJobs, calcDistance,
} from '../services/kakaoService';

export default function KakaoMap({ jobs = [], onJobSelect, style = {} }) {
  const containerRef  = useRef(null);
  const mapRef        = useRef(null);
  const markersRef    = useRef([]);
  const myLocRef      = useRef(null);
  const myPosRef      = useRef(null);

  const [status,        setStatus]        = useState('loading'); // loading | ready | error
  const [errorMsg,      setErrorMsg]      = useState('');
  const [visibleCount,  setVisibleCount]  = useState(0);
  const [searchInBounds, setSearchInBounds] = useState(false);
  const [boundsJobs,    setBoundsJobs]    = useState(jobs);

  // ── 지도 초기화 ─────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await waitForKakao();
        if (cancelled || !containerRef.current) return;

        // 현재 위치 시도
        let center = { lat: 37.5665, lng: 126.978 }; // 기본: 서울시청
        try {
          const pos = await getCurrentPosition();
          center = pos;
          myPosRef.current = pos;
        } catch { /* 위치 권한 없을 경우 기본 좌표 사용 */ }

        const map = createMap(containerRef.current, { ...center, level: 5 });
        mapRef.current = map;

        // 현재 위치 마커
        if (myPosRef.current) {
          myLocRef.current = createCurrentLocationMarker(map, center.lat, center.lng);
        }

        // 지도 이동/줌 종료 후 가시 공고 수 갱신
        window.kakao.maps.event.addListener(map, 'idle', () => {
          const inBounds = getJobsInBounds(map, jobs);
          setVisibleCount(inBounds.length);
          setSearchInBounds(true);
        });

        if (!cancelled) setStatus('ready');
      } catch (e) {
        if (!cancelled) { setErrorMsg(e.message); setStatus('error'); }
      }
    })();

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 공고 마커 갱신 ──────────────────────────────────────────

  useEffect(() => {
    if (!mapRef.current || status !== 'ready') return;
    clearMarkers(markersRef.current);
    markersRef.current = renderJobMarkers(mapRef.current, boundsJobs, onJobSelect);
    setVisibleCount(boundsJobs.length);
  }, [boundsJobs, status, onJobSelect]);

  // jobs prop이 바뀌면 초기 전체 표시
  useEffect(() => {
    setBoundsJobs(jobs);
    setSearchInBounds(false);
  }, [jobs]);

  // ── 현재 위치로 이동 ─────────────────────────────────────────

  const goToMyLocation = useCallback(async () => {
    try {
      const pos = await getCurrentPosition();
      myPosRef.current = pos;
      panTo(mapRef.current, pos.lat, pos.lng, 4);
      if (myLocRef.current) myLocRef.current.setMap(null);
      myLocRef.current = createCurrentLocationMarker(mapRef.current, pos.lat, pos.lng);
    } catch (e) {
      alert(e.message);
    }
  }, []);

  // ── 이 지역 재검색 ───────────────────────────────────────────

  const handleSearchInBounds = useCallback(() => {
    if (!mapRef.current) return;
    const inBounds = getJobsInBounds(mapRef.current, jobs);
    setBoundsJobs(inBounds);
    setSearchInBounds(false);
  }, [jobs]);

  // ── 전체 공고 보기 ───────────────────────────────────────────

  const handleShowAll = useCallback(() => {
    setBoundsJobs(jobs);
    fitBoundsToJobs(mapRef.current, jobs);
    setSearchInBounds(false);
  }, [jobs]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', ...style }}>
      {/* 지도 컨테이너 */}
      <div ref={containerRef} style={{ width: '100%', height: '100%', borderRadius: 0 }} />

      {/* 로딩 */}
      {status === 'loading' && (
        <div style={overlay}>
          <div style={{ fontSize: 36 }}>🗺️</div>
          <div style={{ color: '#888', marginTop: 8, fontSize: 13 }}>카카오 지도 로딩 중...</div>
        </div>
      )}

      {/* 에러 */}
      {status === 'error' && (
        <div style={{ ...overlay, background: '#fff3f3' }}>
          <div style={{ fontSize: 32 }}>⚠️</div>
          <div style={{ color: '#cc0000', marginTop: 8, fontSize: 13, textAlign: 'center', padding: '0 20px' }}>
            {errorMsg}
          </div>
          <div style={{ fontSize: 11, color: '#999', marginTop: 8, textAlign: 'center', padding: '0 20px' }}>
            .env.local의 REACT_APP_KAKAO_MAP_KEY와<br />
            카카오 개발자 콘솔의 도메인 등록을 확인하세요
          </div>
        </div>
      )}

      {status === 'ready' && (
        <>
          {/* 공고 수 뱃지 */}
          <div style={badge}>
            📌 {visibleCount}개 공고
          </div>

          {/* 이 지역 재검색 버튼 */}
          {searchInBounds && (
            <button onClick={handleSearchInBounds} style={searchBtn}>
              🔍 이 지역 재검색
            </button>
          )}

          {/* 전체 보기 버튼 */}
          {boundsJobs.length < jobs.length && (
            <button onClick={handleShowAll} style={{ ...searchBtn, top: searchInBounds ? 80 : 50 }}>
              전체 보기 ({jobs.length})
            </button>
          )}

          {/* 현재 위치 버튼 */}
          <button onClick={goToMyLocation} style={locBtn}>
            📍
          </button>
        </>
      )}
    </div>
  );
}

// 인라인 스타일 상수
const overlay = {
  position: 'absolute', inset: 0,
  background: '#f2f4f7',
  display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center',
};
const badge = {
  position: 'absolute', top: 12, left: 12,
  background: '#1a1a2e', color: '#FFD60A',
  borderRadius: 20, padding: '5px 12px',
  fontSize: 12, fontWeight: 700,
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
};
const searchBtn = {
  position: 'absolute', top: 50, left: '50%',
  transform: 'translateX(-50%)',
  background: '#fff', border: '1px solid #eee',
  borderRadius: 20, padding: '7px 16px',
  fontSize: 13, fontWeight: 700, cursor: 'pointer',
  boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
  whiteSpace: 'nowrap',
};
const locBtn = {
  position: 'absolute', bottom: 20, right: 16,
  width: 44, height: 44, borderRadius: '50%',
  background: '#fff', border: 'none',
  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
  fontSize: 20, cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
